import { object } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import {
  ApiCreateOrgMarket,
  ApiOrgMarket,
  ApiUpdateOrgMarket,
  getOrgMarketQueryKey,
  getOrgMarketsQueryKey,
  getOrgMarketsQueryKeyPrefix,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';

import { useCreateUpdateMutatorsWithQueryCache } from '../reactQueryLib';
import { useKaiProApiClient } from '../useKaiProApiClient';

const getMarketMutators = (kaiProApiClient: KaiProApiClient) => {
  const queryCache = useQueryCache();
  const crudMutators = useCreateUpdateMutatorsWithQueryCache<ApiCreateOrgMarket, ApiUpdateOrgMarket, ApiOrgMarket>(
    {
      apiCreate: kaiProApiClient.createMarket.bind(kaiProApiClient),
      apiUpdate: kaiProApiClient.updateMarket.bind(kaiProApiClient),
      pluralKeyPrefix: getOrgMarketsQueryKeyPrefix(),
      getSingleKey: getOrgMarketQueryKey,
    },
  );

  const extraMutators = useMemo(() => ({
    addContractor: async (orgMarketId: string, orgContractorId: string) => {
      if (!kaiProApiClient.token) throw new Error('No token or orgId');
      const updated = await kaiProApiClient.addContractorToMarket(orgMarketId, orgContractorId);
      queryCache.refetchAll({ prefix: getOrgMarketsQueryKeyPrefix() });
      queryCache.refetchAll({ prefix: getOrgMarketQueryKey(orgMarketId) });
      return updated;
    },
    removeContractor: async (orgMarketId: string, orgContractorId: string) => {
      if (!kaiProApiClient.token) throw new Error('No token or orgId');
      const updated = await kaiProApiClient.removeContractorFromMarket(orgMarketId, orgContractorId);
      queryCache.refetchAll({ prefix: getOrgMarketsQueryKeyPrefix() });
      queryCache.refetchAll({ prefix: getOrgMarketQueryKey(orgMarketId) });
      return updated;
    },
    refetchAllMarkets: () => queryCache.refetchAll({ prefix: getOrgMarketsQueryKeyPrefix() }),
  }), [kaiProApiClient, queryCache]);
  return { ...crudMutators, ...extraMutators };
};

export const useOrgMarkets = (options?: { archived?: boolean; search?: string }) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getOrgMarketsQueryKey(options?.archived, options?.search),
    async (key, queryCache) => {
      const markets = await kaiProApiClient.getOrgMarkets({ archived: options?.archived ? true : undefined, search: options?.search });
      markets.forEach(market => {
        queryCache.cache.set(getOrgMarketQueryKey(market.id), market);
      });
      return markets;
    },
    {
      enabled: !!kaiProApiClient.token,
    },
  );

  return {
    ...query,
    markets: query.data,
    marketsById: query.data ? object(query.data, { withKey: ({ id }) => id }) : undefined,
    marketsMutators: getMarketMutators(kaiProApiClient),
  };
};

export const useOrgMarket = (id?: string | undefined | null) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery<ApiOrgMarket>(
    getOrgMarketQueryKey(id),
    () => kaiProApiClient.getMarket(id!),
    { enabled: !!kaiProApiClient.token && !!id },
  );

  return {
    ...query,
    orgMarket: query.data,
    marketsMutators: getMarketMutators(kaiProApiClient),
  };
};
